import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../services/supabaseClient";

// LOT0F9_ADMIN_PARTNER_CANDIDATES_PANEL_V1
// LOT0F10_CONTROLLED_MERCHANT_ONBOARDING_INVITE_V1

type AnyRecord = Record<string, any>;
type Candidate = AnyRecord & { prp?: AnyRecord | null };
type Decision = "approve" | "return_to_prp" | "reject";
type BusyAction = Decision | "invite";
type InviteStatus = {
  status?: "none" | "active" | "consumed" | "revoked" | "expired" | string;
  expires_at?: string | null;
  invite_email?: string | null;
  email_sent?: boolean | null;
  invite_url?: string | null;
};

const text = (value: unknown, fallback = ""): string =>
  typeof value === "string" && value.trim() ? value.trim() : fallback;

const stageOf = (candidate: Candidate): string =>
  text(candidate.pipeline_stage || candidate.status, "registered").toLowerCase();

async function invokeAdminCandidates(body: Record<string, unknown>) {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  const token = session?.access_token?.trim();
  if (!token) throw new Error("admin_session_required");

  const result = await supabase.functions.invoke("admin-prp-partner-candidates", {
    body,
    headers: { Authorization: `Bearer ${token}` },
  });

  if (result.error) {
    const errorData = result.data as AnyRecord | null;
    throw new Error(
      typeof errorData?.error === "string"
        ? errorData.error
        : result.error.message || "admin_partner_candidate_failed",
    );
  }

  const payload = (result.data || {}) as AnyRecord;
  if (payload.ok !== true) {
    throw new Error(text(payload.error, "admin_partner_candidate_failed"));
  }
  return payload;
}


async function invokeAdminOnboardingInvite(body: Record<string, unknown>) {
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  const token = session?.access_token?.trim();
  if (!token) throw new Error("admin_session_required");

  const result = await supabase.functions.invoke("admin-prp-onboarding-invite", {
    body,
    headers: { Authorization: `Bearer ${token}` },
  });

  if (result.error) {
    const errorData = result.data as AnyRecord | null;
    throw new Error(
      typeof errorData?.error === "string"
        ? errorData.error
        : result.error.message || "admin_onboarding_invite_failed",
    );
  }

  const payload = (result.data || {}) as AnyRecord;
  if (payload.ok !== true) {
    throw new Error(text(payload.error, "admin_onboarding_invite_failed"));
  }
  return payload;
}

export default function AdminPartnerCandidatesPanel() {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "registered" | "approved">("registered");
  const [regionFilter, setRegionFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<BusyAction | "">("");
  const [inviteStatus, setInviteStatus] = useState<InviteStatus | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await invokeAdminCandidates({ action: "list" });
      setCandidates(Array.isArray(payload.candidates) ? payload.candidates : []);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "admin_partner_candidate_list_failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const regions = useMemo(
    () => Array.from(new Set(candidates.map((candidate) => text(candidate.prp?.region)).filter(Boolean)))
      .sort((left, right) => left.localeCompare(right, "fr")),
    [candidates],
  );

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return candidates
      .filter((candidate) => {
        const stage = stageOf(candidate);
        if (statusFilter !== "all" && stage !== statusFilter) return false;
        const region = text(candidate.prp?.region);
        if (regionFilter !== "all" && region !== regionFilter) return false;
        if (!query) return true;
        return [
          candidate.business_name, candidate.contact_name, candidate.email, candidate.phone,
          candidate.prp?.full_name, candidate.prp?.email, region,
        ].filter(Boolean).join(" ").toLowerCase().includes(query);
      })
      .sort((left, right) =>
        new Date(right.updated_at || right.created_at || 0).getTime()
        - new Date(left.updated_at || left.created_at || 0).getTime()
      );
  }, [candidates, regionFilter, search, statusFilter]);

  const selected = useMemo(
    () => candidates.find((candidate) => text(candidate.id) === selectedId) || null,
    [candidates, selectedId],
  );

  useEffect(() => {
    let active = true;

    const loadInviteStatus = async () => {
      if (!selected || stageOf(selected) !== "approved") {
        setInviteStatus(null);
        return;
      }

      setInviteLoading(true);
      try {
        const payload = await invokeAdminOnboardingInvite({
          action: "status",
          lead_id: text(selected.id),
        });
        if (active) {
          setInviteStatus(payload.invite || { status: "none" });
        }
      } catch (caught) {
        if (active) {
          setInviteStatus(null);
          setError(caught instanceof Error ? caught.message : "admin_onboarding_invite_status_failed");
        }
      } finally {
        if (active) setInviteLoading(false);
      }
    };

    void loadInviteStatus();
    return () => { active = false; };
  }, [selectedId, candidates]);

  const pendingCount = candidates.filter((candidate) => stageOf(candidate) === "registered").length;
  const approvedCount = candidates.filter((candidate) => stageOf(candidate) === "approved").length;

  const decide = async (decision: Decision) => {
    if (!selected || busy) return;
    const leadId = text(selected.id);
    if (!leadId) return;

    if (decision !== "approve" && reason.trim().length < 5) {
      setError("Ajoute un motif d’au moins 5 caractères.");
      return;
    }

    const label = decision === "approve"
      ? "approuver"
      : decision === "return_to_prp"
        ? "retourner au PRP"
        : "refuser";

    if (!window.confirm(`Confirmer : ${label} ${text(selected.business_name, "ce candidat")} ?`)) {
      return;
    }

    setBusy(decision);
    setError("");
    try {
      await invokeAdminCandidates({
        action: "decide",
        lead_id: leadId,
        decision,
        reason: reason.trim() || null,
        human_confirmation: true,
      });
      setReason("");
      await load();
      setSelectedId(leadId);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "admin_partner_candidate_decision_failed");
    } finally {
      setBusy("");
    }
  };

  const createOnboardingInvite = async () => {
    if (!selected || stageOf(selected) !== "approved" || busy) return;

    const leadId = text(selected.id);
    if (!leadId) return;

    if (!text(selected.email)) {
      setError("Une adresse courriel est requise avant de créer l’invitation d’onboarding.");
      return;
    }

    if (!window.confirm(`Créer une invitation d’onboarding pour ${text(selected.business_name, "ce partenaire")} ?`)) {
      return;
    }

    setBusy("invite");
    setError("");

    try {
      const payload = await invokeAdminOnboardingInvite({
        action: "create_send",
        lead_id: leadId,
        human_confirmation: true,
      });
      setInviteStatus(payload.invite || null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "admin_onboarding_invite_failed");
    } finally {
      setBusy("");
    }
  };

  const copyInviteLink = async () => {
    const inviteUrl = text(inviteStatus?.invite_url);
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
    } catch {
      window.prompt("Copie ce lien d’onboarding :", inviteUrl);
    }
  };

  return (
    <div className="space-y-6" data-lot0f9-admin-partner-candidates="true">
      <div className="rounded-3xl border border-indigo-100 bg-gradient-to-br from-white to-indigo-50 p-6 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-indigo-600">Validation partenaires PRP</p>
            <h2 className="mt-1 text-2xl font-black text-slate-950">Candidats partenaires</h2>
            <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-600">
              Revois les candidats soumis par les PRP. Une approbation place seulement le prospect à l’étape Approuvé.
              Aucun compte commerce, aucune publication et aucune activation financière ne sont créés ici.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-3">
              <p className="text-2xl font-black text-amber-900">{pendingCount}</p>
              <p className="text-[10px] font-black uppercase text-amber-700">à valider</p>
            </div>
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-3">
              <p className="text-2xl font-black text-emerald-900">{approvedCount}</p>
              <p className="text-[10px] font-black uppercase text-emerald-700">approuvés</p>
            </div>
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-[1fr_220px_220px_auto]">
          <input className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold"
            onChange={(event) => setSearch(event.target.value)} placeholder="Entreprise, contact, PRP, région…" value={search} />
          <select className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black"
            onChange={(event) => setStatusFilter(event.target.value as "all" | "registered" | "approved")}
            value={statusFilter}>
            <option value="registered">En attente</option><option value="approved">Approuvés</option><option value="all">Tous</option>
          </select>
          <select className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black"
            onChange={(event) => setRegionFilter(event.target.value)} value={regionFilter}>
            <option value="all">Toutes les régions</option>
            {regions.map((region) => <option key={region} value={region}>{region}</option>)}
          </select>
          <button className="rounded-xl border border-indigo-200 bg-white px-4 py-3 text-sm font-black text-indigo-700"
            onClick={() => void load()} type="button">Actualiser</button>
        </div>
      </div>

      {error && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-800">{error}</div>}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_430px]">
        <div className="rounded-3xl border border-slate-100 bg-white shadow-sm">
          <div className="border-b border-slate-100 p-5">
            <p className="font-black text-slate-900">File de validation</p>
            <p className="mt-1 text-xs font-semibold text-slate-400">{visible.length} dossier(s) affiché(s)</p>
          </div>
          {loading ? (
            <div className="p-10 text-center text-sm font-bold text-slate-400">Chargement des candidats…</div>
          ) : visible.length === 0 ? (
            <div className="p-10 text-center text-sm font-bold text-slate-400">Aucun candidat pour ces filtres.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {visible.map((candidate) => {
                const id = text(candidate.id);
                const stage = stageOf(candidate);
                return (
                  <button className={`w-full p-5 text-left transition ${selectedId === id ? "bg-indigo-50" : "hover:bg-slate-50"}`}
                    key={id} onClick={() => setSelectedId(id)} type="button">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-black text-slate-950">{text(candidate.business_name, "Entreprise")}</p>
                        <p className="mt-1 text-xs font-semibold text-slate-500">
                          {text(candidate.contact_name, "Contact non identifié")} · {text(candidate.email || candidate.phone, "Coordonnée absente")}
                        </p>
                        <p className="mt-2 text-[11px] font-bold uppercase tracking-wide text-indigo-600">
                          PRP : {text(candidate.prp?.full_name || candidate.prp?.email, "PRP")} · {text(candidate.prp?.region, "Région non définie")}
                        </p>
                      </div>
                      <span className={`rounded-full px-3 py-1 text-[10px] font-black uppercase ${stage === "approved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                        {stage === "approved" ? "Approuvé" : "En attente"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <aside className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
          {!selected ? (
            <div className="flex min-h-80 items-center justify-center text-center">
              <div><p className="text-lg font-black text-slate-900">Dossier administrateur</p>
                <p className="mt-2 text-sm font-semibold text-slate-400">Sélectionne un candidat pour prendre une décision.</p></div>
            </div>
          ) : (
            <div>
              <p className="text-xs font-black uppercase tracking-[0.18em] text-indigo-600">Dossier candidat</p>
              <h3 className="mt-1 text-xl font-black text-slate-950">{text(selected.business_name, "Entreprise")}</h3>
              <div className="mt-4 grid gap-2 text-sm">
                <div className="rounded-xl bg-slate-50 p-3"><p className="text-[10px] font-black uppercase text-slate-400">Contact</p>
                  <p className="mt-1 font-bold text-slate-800">{text(selected.contact_name, "Non identifié")}</p>
                  <p className="text-xs font-semibold text-slate-500">{text(selected.email || selected.phone, "Coordonnée absente")}</p></div>
                <div className="rounded-xl bg-indigo-50 p-3"><p className="text-[10px] font-black uppercase text-indigo-500">PRP responsable</p>
                  <p className="mt-1 font-bold text-indigo-950">{text(selected.prp?.full_name || selected.prp?.email, "PRP")}</p>
                  <p className="text-xs font-semibold text-indigo-700">{text(selected.prp?.region, "Région non définie")}</p></div>
                <div className="rounded-xl bg-slate-50 p-3"><p className="text-[10px] font-black uppercase text-slate-400">Prochaine action PRP</p>
                  <p className="mt-1 text-sm font-semibold text-slate-700">{text(selected.next_action, "Aucune prochaine action")}</p></div>
              </div>

              {stageOf(selected) === "registered" ? (
                <>
                  <textarea className="mt-4 min-h-24 w-full rounded-xl border border-slate-200 p-3 text-sm font-semibold"
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="Motif requis pour retour/refus. Optionnel pour approbation." value={reason} />
                  <div className="mt-3 grid gap-2">
                    <button className="rounded-xl bg-emerald-600 px-4 py-3 text-sm font-black text-white disabled:opacity-40"
                      disabled={Boolean(busy)} onClick={() => void decide("approve")} type="button">
                      {busy === "approve" ? "Approbation…" : "✓ Approuver le candidat"}</button>
                    <button className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-black text-amber-800 disabled:opacity-40"
                      disabled={Boolean(busy)} onClick={() => void decide("return_to_prp")} type="button">
                      {busy === "return_to_prp" ? "Retour…" : "↩ Retourner au PRP"}</button>
                    <button className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-black text-red-700 disabled:opacity-40"
                      disabled={Boolean(busy)} onClick={() => void decide("reject")} type="button">
                      {busy === "reject" ? "Refus…" : "✕ Refuser le candidat"}</button>
                  </div>
                </>
              ) : (
                <div className="mt-4 space-y-3">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                    <p className="font-black text-emerald-900">✓ Candidat approuvé</p>
                    <p className="mt-1 text-xs font-semibold leading-5 text-emerald-800">
                      L’approbation CRM est enregistrée. Le partenaire doit encore créer ou utiliser son profil Forfeo et soumettre sa demande commerçante.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4" data-lot0f10-admin-invite="true">
                    <p className="font-black text-blue-950">Invitation d’onboarding commerçant</p>
                    <p className="mt-1 text-xs font-semibold leading-5 text-blue-800">
                      Un lien sécurisé de 14 jours préremplit la demande commerçante et conserve l’attribution PRP. L’invitation elle-même ne crée aucun commerce.
                    </p>

                    {inviteLoading ? (
                      <p className="mt-3 text-xs font-bold text-blue-700">Vérification de l’invitation…</p>
                    ) : (
                      <div className="mt-3 rounded-xl bg-white/80 p-3 text-xs font-bold text-blue-900">
                        État : {text(inviteStatus?.status, "aucune invitation")}
                        {inviteStatus?.expires_at ? ` · expire ${new Date(inviteStatus.expires_at).toLocaleDateString("fr-CA")}` : ""}
                        {inviteStatus?.email_sent === true ? " · courriel envoyé" : ""}
                      </div>
                    )}

                    {inviteStatus?.status !== "consumed" && (
                      <button
                        className="mt-3 w-full rounded-xl bg-blue-700 px-4 py-3 text-sm font-black text-white disabled:opacity-40"
                        disabled={Boolean(busy)}
                        onClick={() => void createOnboardingInvite()}
                        type="button"
                      >
                        {busy === "invite" ? "Création…" : "Créer / envoyer l’invitation"}
                      </button>
                    )}

                    {inviteStatus?.invite_url && (
                      <button
                        className="mt-2 w-full rounded-xl border border-blue-200 bg-white px-4 py-3 text-sm font-black text-blue-800"
                        onClick={() => void copyInviteLink()}
                        type="button"
                      >
                        Copier le lien sécurisé
                      </button>
                    )}

                    {inviteStatus?.status === "consumed" && (
                      <p className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs font-black text-emerald-800">
                        ✓ Invitation réclamée : le partenaire a transmis sa demande commerçante.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </aside>
      </div>

      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs font-bold leading-5 text-amber-900">
        Garde-fou : cette console modifie uniquement l’état CRM du candidat. Elle ne crée aucun commerce, ne publie aucune offre,
        n’active aucun abonnement, ne touche pas Stripe/Giftshop et n’accorde aucune autorité financière.
      </div>
    </div>
  );
}
